import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	Loader2,
	AlertCircle,
	Calendar,
	CreditCard,
	Sparkles,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { useAuth } from "@/lib/contexts/auth.context";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { SubscriptionService } from "@/lib/subscription/subscription.service";
import { TrialService } from "@/lib/subscription/trial.service";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * Subscription Management Component
 * Shows current subscription status and allows cancellation/reactivation
 */
const SubscriptionManagement = () => {
	const { t } = useTranslation();
	const { subscriptionStatus, limits, refreshUsage, currentPlanId } =
		useSubscription();
	const { refreshUserProfile } = useAuth();
	const [canceling, setCanceling] = useState(false);
	const [reactivating, setReactivating] = useState(false);
	const [showCancelDialog, setShowCancelDialog] = useState(false);
	const [switchingToStarter, setSwitchingToStarter] = useState(false);
	const [currentPeriodEnd, setCurrentPeriodEnd] = useState<Date | null>(null);
	const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<Date | null>(null);
	const [hasScheduledDowngrade, setHasScheduledDowngrade] = useState(false);
	const [scheduledPlanName, setScheduledPlanName] = useState<string | null>(
		null,
	);
	const [trialEndDate, setTrialEndDate] = useState<Date | null>(null);
	const [loading, setLoading] = useState(true);

	const { data: plans } = useQuery({
		queryKey: ["subscription-plans"],
		queryFn: () => SubscriptionService.getPlans(),
		staleTime: 1000 * 60 * 30,
	});

	const currentPlan = plans?.find((p) => p.id === currentPlanId);
	const isCreatorPlan = currentPlan?.name === "Creator";
	const showCreatorCancelIntercept = isCreatorPlan && !hasScheduledDowngrade;
	const isTrialing = subscriptionStatus === "TRIALING";

	// Fetch subscription details on mount
	useEffect(() => {
		const fetchDetails = async () => {
			try {
				setLoading(true);
				const details = await SubscriptionService.getSubscriptionDetails();
				setCurrentPeriodEnd(details.currentPeriodEnd);
				if (details.cancelAtPeriodEnd && details.currentPeriodEnd) {
					setCancelAtPeriodEnd(details.currentPeriodEnd);
				} else {
					setCancelAtPeriodEnd(null);
				}
				setHasScheduledDowngrade(details.hasScheduledDowngrade);
				setScheduledPlanName(details.scheduledPlanName);
				// Set trial end date if user is trialing
				if (details.status === "trialing" && details.currentPeriodEnd) {
					setTrialEndDate(details.currentPeriodEnd);
				}
			} catch (error) {
				console.error("Failed to fetch subscription details:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchDetails();
	}, []);

	// Only show for paid plans
	const isPaidPlan =
		subscriptionStatus &&
		subscriptionStatus !== "NONE" &&
		subscriptionStatus !== "CANCELED" &&
		subscriptionStatus !== "INCOMPLETE_EXPIRED";

	if (!isPaidPlan || loading) {
		return null;
	}

	const handleCancelSubscription = async () => {
		try {
			setCanceling(true);
			AnalyticsService.track("cancel_intercept_declined", {
				plan: isCreatorPlan ? "creator" : (currentPlan?.name ?? "unknown"),
			});
			const response = await SubscriptionService.cancelSubscription();

			if (response.success) {
				toast.success(t("pricing.subscription.toasts.cancelSuccess"));
				setCancelAtPeriodEnd(response.currentPeriodEnd);
				AnalyticsService.track("subscription_canceled", {
					plan_name: currentPlanId ?? undefined,
				});
				await refreshUsage();
			}
		} catch (error) {
			console.error("Failed to cancel subscription:", error);
			toast.error(t("pricing.subscription.toasts.cancelFailed"));
		} finally {
			setCanceling(false);
			setShowCancelDialog(false);
		}
	};

	const handleSwitchToStarter = async () => {
		try {
			setSwitchingToStarter(true);
			AnalyticsService.track("cancel_intercept_converted", {
				from: "Creator",
				to: "Starter",
			});

			if (isTrialing) {
				// Trial -> immediate cancel + fresh Starter sub
				await TrialService.switchTrialToStarter();
			} else {
				// Active -> downgrade scheduled to period end via existing flow
				const starter = plans?.find((p) => p.name === "Starter");
				if (!starter?.stripePriceId) {
					throw new Error("Starter plan has no Stripe price ID");
				}
				await SubscriptionService.downgradeSubscription(starter.stripePriceId);
			}

			toast.success(t("pricing.subscription.cancelIntercept.toasts.switched"));
			await refreshUserProfile();
			await refreshUsage();
			if (!isTrialing) {
				setHasScheduledDowngrade(true);
				setScheduledPlanName("Starter");
			}
			setShowCancelDialog(false);
		} catch (error) {
			console.error("Failed to switch to Starter:", error);
			toast.error(
				t("pricing.subscription.cancelIntercept.toasts.switchFailed"),
			);
		} finally {
			setSwitchingToStarter(false);
		}
	};

	const handleOpenCancel = () => {
		if (showCreatorCancelIntercept) {
			AnalyticsService.track("cancel_intercept_shown", { plan: "creator" });
		}
		setShowCancelDialog(true);
	};

	const handleReactivateSubscription = async () => {
		try {
			setReactivating(true);
			const response = await SubscriptionService.reactivateSubscription();

			if (response.success) {
				toast.success(t("pricing.subscription.toasts.reactivateSuccess"));
				setCancelAtPeriodEnd(null);
				await refreshUsage();
			}
		} catch (error) {
			console.error("Failed to reactivate subscription:", error);
			toast.error(t("pricing.subscription.toasts.reactivateFailed"));
		} finally {
			setReactivating(false);
		}
	};

	const statusColors = {
		ACTIVE: "text-emerald-400",
		TRIALING: "text-sky-400",
		PAST_DUE: "text-amber-400",
		UNPAID: "text-destructive",
	};

	const statusColor =
		statusColors[subscriptionStatus as keyof typeof statusColors] ||
		"text-muted-foreground";

	return (
		<div className="w-full bg-card border border-border rounded-xl p-6 mb-8 shadow-sm">
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				{/* Subscription Info */}
				<div className="flex-1">
					<div className="flex items-center gap-2 mb-2">
						<CreditCard className="h-5 w-5 text-primary" />
						<h3 className="text-lg font-semibold text-foreground">
							{t("pricing.subscription.management.title")}
						</h3>
					</div>

					<div className="space-y-1 text-sm">
						<p className="text-muted-foreground">
							{t("pricing.subscription.management.status")}:{" "}
							<span className={`font-medium ${statusColor}`}>
								{t(`pricing.subscription.status.${subscriptionStatus}`)}
							</span>
						</p>

						{limits && (
							<p className="text-muted-foreground">
								{t("pricing.subscription.management.coins")}:{" "}
								<span className="font-medium text-foreground">
									{limits.monthlyCoins}{" "}
									{t("pricing.subscription.management.coinsPerMonth")}
								</span>
							</p>
						)}

						{/* Show trial end date */}
						{subscriptionStatus === "TRIALING" && trialEndDate && (
							<Alert className="mt-3 border-emerald-500/30 bg-emerald-500/5">
								<Sparkles className="h-4 w-4 text-emerald-500" />
								<AlertDescription className="text-emerald-300 text-xs">
									<div className="flex items-center gap-1">
										<Calendar className="h-3 w-3" />
										{t("pricing.subscription.management.trialEndsAt", {
											date: trialEndDate.toLocaleDateString(),
										})}
									</div>
								</AlertDescription>
							</Alert>
						)}

						{/* Show scheduled downgrade message */}
						{hasScheduledDowngrade &&
							!cancelAtPeriodEnd &&
							currentPeriodEnd &&
							scheduledPlanName && (
								<Alert className="mt-3 border-sky-500/30 bg-sky-500/5">
									<AlertCircle className="h-4 w-4 text-sky-500" />
									<AlertDescription className="text-sky-300 text-xs">
										<div className="flex items-center gap-1">
											<Calendar className="h-3 w-3" />
											{t("pricing.subscription.management.downgradeScheduled", {
												plan: scheduledPlanName,
												date: currentPeriodEnd.toLocaleDateString(),
											})}
										</div>
									</AlertDescription>
								</Alert>
							)}

						{/* Show cancellation message */}
						{cancelAtPeriodEnd && (
							<Alert className="mt-3 border-amber-500/30 bg-amber-500/5">
								<AlertCircle className="h-4 w-4 text-amber-500" />
								<AlertDescription className="text-amber-300 text-xs">
									<div className="flex items-center gap-1">
										<Calendar className="h-3 w-3" />
										{t("pricing.subscription.management.cancelScheduled", {
											date: cancelAtPeriodEnd.toLocaleDateString(),
										})}
									</div>
								</AlertDescription>
							</Alert>
						)}
					</div>
				</div>

				{/* Action Buttons */}
				<div className="flex flex-wrap gap-2">
					{cancelAtPeriodEnd ? (
						<Button
							onClick={handleReactivateSubscription}
							disabled={reactivating}
							variant="default"
							size="sm"
						>
							{reactivating ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{t("common.processing")}
								</>
							) : (
								t("pricing.subscription.management.reactivate")
							)}
						</Button>
					) : (
						<Button
							onClick={handleOpenCancel}
							disabled={canceling}
							variant="outline"
							size="sm"
							className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
						>
							{subscriptionStatus === "TRIALING"
								? t("pricing.subscription.management.cancelTrial")
								: t("pricing.subscription.management.cancel")}
						</Button>
					)}
				</div>
			</div>

			{/* Cancel Confirmation Dialog */}
			<Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
				<DialogContent>
					{showCreatorCancelIntercept ? (
						<>
							<DialogHeader>
								<DialogTitle>
									{t("pricing.subscription.cancelIntercept.title")}
								</DialogTitle>
								<DialogDescription>
									{t("pricing.subscription.cancelIntercept.description")}
								</DialogDescription>
							</DialogHeader>

							<div className="flex flex-col gap-3 mt-2">
								<Button
									type="button"
									onClick={handleSwitchToStarter}
									disabled={switchingToStarter || canceling}
								>
									{switchingToStarter ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{t("common.processing")}
										</>
									) : (
										t("pricing.subscription.cancelIntercept.cta.switchStarter")
									)}
								</Button>
								<p className="text-xs text-muted-foreground -mt-1.5 text-center">
									{t("pricing.subscription.cancelIntercept.starterPerk")}
								</p>

								<Button
									type="button"
									variant="secondary"
									onClick={() => setShowCancelDialog(false)}
									disabled={switchingToStarter || canceling}
								>
									{t("pricing.subscription.cancelIntercept.cta.stay")}
								</Button>

								<button
									type="button"
									onClick={handleCancelSubscription}
									disabled={switchingToStarter || canceling}
									className="text-xs text-destructive/80 underline-offset-4 hover:underline hover:text-destructive disabled:opacity-50"
								>
									{canceling ? (
										<>
											<Loader2 className="inline-block mr-1 h-3 w-3 animate-spin" />
											{t("common.processing")}
										</>
									) : (
										t("pricing.subscription.cancelIntercept.cta.cancelAnyway")
									)}
								</button>
							</div>
						</>
					) : (
						<>
							<DialogHeader>
								<DialogTitle>
									{subscriptionStatus === "TRIALING"
										? t(
												"pricing.subscription.management.cancelDialog.trialTitle",
											)
										: t("pricing.subscription.management.cancelDialog.title")}
								</DialogTitle>
								<DialogDescription>
									{subscriptionStatus === "TRIALING"
										? t(
												"pricing.subscription.management.cancelDialog.trialDescription",
											)
										: t(
												"pricing.subscription.management.cancelDialog.description",
											)}
								</DialogDescription>
							</DialogHeader>

							<Alert className="border-amber-500/30 bg-amber-500/5">
								<AlertCircle className="h-4 w-4 text-amber-500" />
								<AlertDescription className="text-amber-300 text-sm">
									{subscriptionStatus === "TRIALING"
										? t(
												"pricing.subscription.management.cancelDialog.trialWarning",
											)
										: t("pricing.subscription.management.cancelDialog.warning")}
								</AlertDescription>
							</Alert>

							<DialogFooter>
								<Button
									variant="outline"
									onClick={() => setShowCancelDialog(false)}
									disabled={canceling}
								>
									{t("common.close")}
								</Button>
								<Button
									variant="destructive"
									onClick={handleCancelSubscription}
									disabled={canceling}
								>
									{canceling ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{t("common.processing")}
										</>
									) : subscriptionStatus === "TRIALING" ? (
										t(
											"pricing.subscription.management.cancelDialog.trialConfirm",
										)
									) : (
										t("pricing.subscription.management.cancelDialog.confirm")
									)}
								</Button>
							</DialogFooter>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default SubscriptionManagement;
